import { Box } from "@mui/material";
import { useState } from "react";
import { getContrastTextColor } from "../utils/colorManager";
import { initialsFor, markColour } from "../utils/mark";

/**
 * A tenant's or service's identity: its configured logo when there is one, and
 * otherwise its initials in a colour derived from those initials.
 *
 * The fallback is the click dummy's `tile()`, made real: the design draws every
 * corporate as initials on a coloured square, and a tenant with no logo
 * configured would otherwise leave an empty space where its brand belongs.
 */
const BrandMark = ({
  name,
  logoUrl,
  size = 38,
  radius = "10px",
  brand = false,
  announce = false,
}: {
  name: string;
  logoUrl?: string;
  size?: number;
  radius?: string;
  // The portal's own crest: white tile, primary-coloured letters, as the dummy
  // draws it. Used where there is no tenant to name.
  brand?: boolean;
  // Named for a screen reader where the mark is the ONLY thing naming the
  // tenant (the header). Beside a visible name it stays decorative.
  announce?: boolean;
}) => {
  // A configured logo that does not load falls back to the same mark a
  // missing one gets, rather than to a broken image.
  const [failed, setFailed] = useState(false);

  if (logoUrl && !failed) {
    return (
      <Box
        component="img"
        src={logoUrl}
        alt={name}
        onError={() => setFailed(true)}
        sx={{ height: size, maxWidth: size * 3.2, objectFit: "contain" }}
      />
    );
  }

  const initials = initialsFor(name);
  const colour = markColour(initials);

  return (
    <Box
      component="span"
      role={announce ? "img" : undefined}
      aria-label={announce ? name : undefined}
      aria-hidden={announce ? undefined : true}
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        borderRadius: radius,
        bgcolor: brand ? "background.paper" : colour,
        color: brand ? "primary.main" : getContrastTextColor(colour),
        fontWeight: 800,
        fontSize: Math.round(size * 0.32),
        lineHeight: 1,
        letterSpacing: ".02em",
      }}
    >
      {initials}
    </Box>
  );
};

export default BrandMark;
